import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { colors, fonts } from "./theme";
import { PhoneLink } from "./ui";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Catalogue", to: "/catalogue" },
  { label: "Brands", to: "/brands" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

function isActive(pathname, to) {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

// The burger only takes over at 960px, so these six labels are still the nav
// on a landscape tablet — i.e. they get tapped. As a bare text run each was a
// ~20px-tall target. The <a> is now a 44px flex box; the underline moves to an
// inner <span> (see below) so it keeps hugging the word instead of sitting a
// dozen pixels beneath it. Nothing shifts vertically: the row is already 44px
// tall because of the Enquire button, and the centred span lands on the same
// baseline the 20px box did.
function navLinkStyle(active) {
  return {
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    minHeight: "44px",
    fontSize: "var(--fs-sm)",
    fontWeight: 500,
    color: active ? colors.ink : colors.muted,
  };
}

// Carries the active underline. It cannot live on the <a> any more: a
// border-bottom on a 44px-tall box paints at the bottom of the box, not under
// the text.
function navUnderlineStyle(active) {
  return {
    paddingBottom: "3px",
    borderBottom: active
      ? `2px solid ${colors.brown}`
      : "2px solid transparent",
  };
}

export default function Header() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu whenever the route changes.
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          background: "rgba(246,241,233,0.92)",
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <div
          className="se-head"
          style={{
            maxWidth: "1180px",
            margin: "0 auto",
            // 28px of fixed gutter either side plus a fixed 32px gap left the
            // brand and the right-hand cluster 264px to share at 320px, and
            // the cluster (Enquire + burger) cannot shrink. Both go fluid so
            // the row still fits on the narrowest phone in the field.
            padding: "14px var(--sp-gutter)",
            display: "flex",
            alignItems: "center",
            gap: "clamp(12px,3vw,32px)",
          }}
        >
          <Link
            to="/"
            style={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              // The wordmark stack is 34px tall on its own, so the home link —
              // which is the masthead's primary target — sat 10px under the
              // minimum. The row beside it is already 44px because of the
              // Enquire button and the burger, so this costs no header height
              // at any width; it only grows the hit box to match its
              // neighbours.
              minHeight: "44px",
              lineHeight: 1.05,
              marginRight: "auto",
            }}
          >
            <span
              className="se-brand"
              style={{
                fontFamily: fonts.serif,
                fontSize: "23px",
                fontWeight: 600,
                letterSpacing: "0.01em",
                color: colors.ink,
              }}
            >
              Sigma Enterprises
            </span>
            <span
              style={{
                fontFamily: fonts.mono,
                fontSize: "var(--fs-tag)",
                // 25 characters at 0.26em tracking made this — not the
                // wordmark above it — the widest element in the masthead, so
                // it wrapped to two lines between 360 and 414px and took the
                // header height with it. 0.16em is the tracking the shared
                // eyebrow style already uses, and it buys back ~14px.
                letterSpacing: "0.16em",
                color: colors.brown,
                textTransform: "uppercase",
                marginTop: "3px",
              }}
            >
              Paper &amp; Board Distributor
            </span>
          </Link>

          <nav
            className="se-nav"
            // The labels now sit on --fs-sm, which is 14px on a phone but
            // 14.7px at the 960px burger swap. A fixed 26px gap on top of that
            // ate most of the ~27px the row had spare at that width, so the
            // gap tightens with it and only reaches 26px once the layout is
            // past its 1180px cap.
            style={{ display: "flex", alignItems: "center", gap: "clamp(18px,2.2vw,26px)" }}
          >
            {NAV.map((n) => {
              const active = isActive(pathname, n.to);
              return (
                <Link key={n.to} to={n.to} style={navLinkStyle(active)}>
                  <span style={navUnderlineStyle(active)}>{n.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* This cluster is the part of the row that cannot shrink — a nowrap
              button next to a fixed-size burger — so its own gap gives way
              first on a narrow screen rather than squeezing the wordmark. */}
          <div style={{ display: "flex", alignItems: "center", gap: "clamp(10px,2.5vw,16px)" }}>
            <PhoneLink
              className="se-phone"
              style={{
                display: "flex",
                alignItems: "center",
                // Visible from 961px up, which still includes touch tablets.
                // The row is already 44px tall, so this costs no height.
                minHeight: "44px",
                fontFamily: fonts.mono,
                fontSize: "var(--fs-meta)",
                color: colors.muted,
                whiteSpace: "nowrap",
              }}
            />
            <Link
              to="/contact"
              className="se-btn-dark"
              style={{
                display: "inline-flex",
                alignItems: "center",
                // 10px/13px measured 38px tall. --btn-py is the padding every
                // other button on the site scales on, and minHeight states the
                // 44px floor here rather than leaning on the one .se-btn-dark
                // sets globally. The label stays on --fs-meta instead of
                // --btn-fs: this is a compact secondary CTA sitting beside
                // 14px nav labels, and 16px would restyle the masthead.
                padding: "var(--btn-py) var(--btn-px)",
                minHeight: "44px",
                borderRadius: "2px",
                fontSize: "var(--fs-meta)",
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Enquire
            </Link>
            <button
              className="se-menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
              // The only nav control that exists below 960px, and it was
              // 40x38 — under the 44px floor in both axes.
              aria-expanded={menuOpen}
              style={{
                alignItems: "center",
                justifyContent: "center",
                width: "44px",
                height: "44px",
                border: `1px solid #B8A98C`,
                borderRadius: "2px",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <span
                style={{
                  display: "block",
                  width: "16px",
                  height: "1.6px",
                  background: colors.ink,
                  boxShadow: `0 5px 0 ${colors.ink},0 -5px 0 ${colors.ink}`,
                }}
              />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            style={{
              // The panel used to be a sibling of <header> with its own
              // `position: sticky; top: 56px`. 56px was a guess — the masthead
              // is 73px on most phones and 93px at 320px once the wordmark
              // wraps — so the panel slid under it (z-index 29 vs 30) as soon
              // as the page scrolled. Living *inside* the sticky header, it
              // needs no offset and no z-index at all: the row and the panel
              // stick together as one block, at any header height.
              background: colors.panel,
              display: "flex",
              flexDirection: "column",
              // Same gutter as the header row above, or the menu labels stop
              // lining up with the wordmark once the gutter goes fluid.
              padding: "4px var(--sp-gutter) 12px",
            }}
          >
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                style={{
                  padding: "14px 0",
                  borderBottom: `1px solid ${colors.border}`,
                  fontSize: "var(--fs-base)",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {n.label}
              </Link>
            ))}
            <PhoneLink
              style={{
                // Was 13px/0/4px around a 14px line — a 35px target on the one
                // number a phone user is most likely to be reaching for.
                padding: "14px 0",
                fontFamily: fonts.mono,
                fontSize: "var(--fs-body)",
                color: colors.brown,
              }}
            />
          </div>
        )}
      </header>
    </>
  );
}
